import { translate } from "@kaltura/ds-react-utils";
import { ChannelData } from "../../types";
import {
    StyledCard,
    StyledThumbnail,
    StyledInfo,
    StyledLink,
    Separator,
    StyledActions,
    StyledMediaCount,
} from "./styled-components";
import { Button, Typography } from "@kaltura/ds-react-components";
import { Subscribe } from "@kaltura/mediaspace-shared-subscribe";
import { forwardRef, useContext, useEffect, useState } from "react";
import { ChannelsContext } from "../../../../ChannelsContext";
import { HtmlReactParser, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { SpanSrOnly, TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

type CardProps = ChannelData & {
    ariaColIndex?: number;
};

const Card = forwardRef<HTMLDivElement, CardProps>((props, ref) => {
    const { id, name, members, subscribers, thumbnailUrl, channelUrl, isUserSubscribed, mediaCount, ariaColIndex } =
        props;

    const { subscribeUrl, unsubscribeUrl } = useContext(ChannelsContext);

    const [subscribersCount, setSubscribersCount] = useState(subscribers ?? 0);

    const baseClassName = "kms-ds-channels-card-";

    const updateSubscribers = (isSubscribed: boolean) => {
        setSubscribersCount((prev) => prev + (isSubscribed ? 1 : -1));
    };

    useEffect(() => {
        // reset subscribers count state when new data comes from the parent component
        if (subscribers !== undefined) {
            setSubscribersCount(subscribers);
        }
    }, [subscribers]);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleNavigateToChannel = () => {
        sendButtonAnalytics("Channel link", ButtonClickAnalyticsType.THUMBNAIL);
    };

    return (
        <StyledCard
            className={`${baseClassName}-container`}
            tabIndex={0}
            aria-labelledby={`sr-label-${id} sr-media-count-${id} sr-info-${id}`}
            aria-colindex={ariaColIndex ?? undefined}
            role="gridcell"
            ref={ref}
        >
            <SpanSrOnly id={`sr-label-${id}`}>{translate("channel %1 card", [name])}</SpanSrOnly>
            <StyledThumbnail
                style={{ backgroundImage: `url("${thumbnailUrl}")` }}
                className={`${baseClassName}thumbnail`}
                href={channelUrl}
                onClick={handleNavigateToChannel}
                aria-label={translate("Go to channel %1", [name])}
            >
                {mediaCount !== undefined && (
                    <StyledMediaCount
                        aria-hidden={true}
                        id={`sr-media-count-${id}`}
                        className={`${baseClassName}media-count`}
                    >
                        {translatePlural("%1 Media", "%1 Media", [mediaCount])}
                    </StyledMediaCount>
                )}
            </StyledThumbnail>
            <StyledLink
                href={channelUrl}
                onClick={handleNavigateToChannel}
                aria-describedby={`sr-link-label-${id}`}
                className={`${baseClassName}name`}
            >
                <TruncateWithTooltip text={HtmlReactParser(name)}>
                    <Typography variant={"h4"} component={"h2"}>
                        {name}
                    </Typography>
                </TruncateWithTooltip>
            </StyledLink>
            <SpanSrOnly id={`sr-link-label-${id}`} className={`${baseClassName}go-to-channel`}>
                {translate("Go to channel %1", [name])}
            </SpanSrOnly>
            <StyledInfo id={`sr-info-${id}`} className={`${baseClassName}info`}>
                {members !== undefined && translatePlural("%1 Member", "%1 Members", [members])}
                {members !== undefined && subscribers !== undefined && (
                    <Separator className={`${baseClassName}separator`} />
                )}
                {subscribers !== undefined && translatePlural("%1 Subscriber", "%1 Subscribers", [subscribersCount])}
            </StyledInfo>
            <StyledActions className={`${baseClassName}actions`}>
                <Button
                    aria-label={translate("Go to channel %1", [name])}
                    href={channelUrl}
                    onClick={handleNavigateToChannel}
                    className={`${baseClassName}go-to-channel-button`}
                >
                    {translate("Go to channel")}
                </Button>
                {subscribers !== undefined && isUserSubscribed !== undefined && (
                    <Subscribe
                        channelId={id}
                        subscribeUrl={subscribeUrl}
                        unsubscribeUrl={unsubscribeUrl}
                        subscribed={isUserSubscribed}
                        isBorderless={true}
                        onSubscribe={updateSubscribers}
                    />
                )}
            </StyledActions>
        </StyledCard>
    );
});

export default Card;
